/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-redeclare */
import {
  cleanInputCreate,
  cleanInputUpdate,
  getReadId,
  getTableClientId,
  UnitInventory,
  syncUnitInventories as syncQuery,
  createUnitInventory as createMutation,
  updateUnitInventory as updateMutation,
  CreateUnitInventoryMutationVariables as CreateMutationVariables,
  CreateUnitInventoryInput as CreateInput,
  deleteUnitInventory as deleteUnitInventoryMutation,
  Model,
  isNilOrEmpty,
  OnCreateUnitInventorySubscription,
  OnUpdateUnitInventorySubscription,
  OnDeleteUnitInventorySubscription,
  onCreateUnitInventory,
  onUpdateUnitInventory,
  onDeleteUnitInventory,
  DateTime,
  fromDateTime,
  calendarDateTimeFromTimestamp,
  isInstantAfterOrEqual,
  todayDateTime,
} from '@rentguru/commons-utils';
import {
  deleteAndHideEntity,
  getFilterFieldNameForIndex,
  list,
  mutation,
  recordWasUpdated,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { useUnits } from './UnitsContext';
import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

const ENTITY_MODEL_NAME: Model = 'UnitInventory';

export type ActionUnitInventory =
  | {
      type: 'SHOULD_FETCH_UNIT_INVENTORY' | 'IS_FETCHING_UNIT_INVENTORY';
    }
  | {
      type: 'ADD_UNIT_INVENTORY';
      payload: { unitInventory: UnitInventory };
    }
  | {
      type: 'FETCHED_UNIT_INVENTORY';
      payload: { unitInventories: UnitInventory[] };
    }
  | {
      type: 'UPDATE_UNIT_INVENTORY';
      payload: { unitInventory: UnitInventory };
    }
  | {
      type: 'DELETE_UNIT_INVENTORY';
      payload: { id: string };
    }
  | {
      type: 'FETCH_FAILED_UNIT_INVENTORY';
    };

export interface UnitInventoryContext extends UnitInventoryState {
  getUnitInventory: (id: string) => UnitInventory | undefined;
  getUnitInventoriesFor: (unitId: string, date?: DateTime) => UnitInventory[];
  createUnitInventory: (input: Omit<UnitInventory, 'id' | 'clientId' | 'readId'>) => Promise<UnitInventory>;
  updateUnitInventory: (original: UnitInventory, updates: Partial<UnitInventory>) => Promise<UnitInventory>;
  deleteUnitInventory: (id: string, flaggedDate?: DateTime) => Promise<void>;
  unitInventoriesLoading: boolean | undefined;
  unitInventoriesError: string | undefined;
  setFetchUnitInventory: () => void;
}

interface UnitInventoryState {
  unitInventories: UnitInventory[] | null;
  loading: boolean;
  shouldFetch: boolean;
  lastFetch: DateTime | null;
}

const unitInventoryInitialState: UnitInventoryState = {
  loading: false,
  unitInventories: null,
  shouldFetch: false,
  lastFetch: null,
};

const unitInventoryReducer = (state: UnitInventoryState, action: ActionUnitInventory): UnitInventoryState => {
  switch (action.type) {
    case 'SHOULD_FETCH_UNIT_INVENTORY':
      if (state.loading || state.shouldFetch) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
      };
    case 'IS_FETCHING_UNIT_INVENTORY':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHED_UNIT_INVENTORY':
      return {
        ...state,

        unitInventories: action.payload.unitInventories,
        loading: false,
        shouldFetch: false,
        lastFetch: todayDateTime(),
      };
    case 'FETCH_FAILED_UNIT_INVENTORY':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
      };
    case 'ADD_UNIT_INVENTORY':
      // Check if already present - If already added by this user or coming from another user
      if (state.unitInventories?.find((object) => object.id === action.payload.unitInventory.id)) {
        return state;
      }

      return {
        ...state,

        unitInventories: [...(state.unitInventories ?? []), action.payload.unitInventory],
      };
    case 'UPDATE_UNIT_INVENTORY':
      // No data
      if (isNilOrEmpty(state.unitInventories)) {
        return state;
      }

      // Already present and same object
      const currentObject = state.unitInventories?.find((object) => object.id === action.payload.unitInventory.id);
      if (!currentObject) {
        return {
          ...state,

          unitInventories: [...state.unitInventories, action.payload.unitInventory],
        };
      }
      if (!recordWasUpdated(currentObject, action.payload.unitInventory)) {
        return state;
      }

      // Update
      return {
        ...state,
        unitInventories: state.unitInventories.map((object) => {
          if (object.id === action.payload.unitInventory.id) {
            return action.payload.unitInventory;
          }
          return object;
        }),
      };
    case 'DELETE_UNIT_INVENTORY':
      if (isNilOrEmpty(state.unitInventories)) {
        return state;
      }
      return {
        ...state,
        unitInventories: state.unitInventories.filter((object) => object.id !== action.payload.id),
      };

    default:
      return state;
  }
};

const fetchUnitInventories = async (clientId: string, additionalFilter?: object): Promise<UnitInventory[]> => {
  return await list<UnitInventory>(
    syncQuery,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME),
    additionalFilter
  );
};

export const UnitInventoryContext = React.createContext<UnitInventoryContext | null>(null);

export const UnitInventoryContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [unitInventoriesState, unitInventoriesDispatch] = useReducer<Reducer<UnitInventoryState, ActionUnitInventory>>(
    unitInventoryReducer,
    unitInventoryInitialState
  );
  const { buildingsUnitsDetailsDelete } = usePermissions();
  const { getUnit } = useUnits();
  const { clientId } = useUser();

  const { loading: unitInventoriesLoading, unitInventories } = unitInventoriesState;

  useEffect(() => {
    const fetchAndSet = async () => {
      unitInventoriesDispatch({ type: 'IS_FETCHING_UNIT_INVENTORY' });
      try {
        const result = await fetchUnitInventories(clientId!);
        unitInventoriesDispatch({ type: 'FETCHED_UNIT_INVENTORY', payload: { unitInventories: result } });
      } catch (error) {
        console.error('[unitInventories] fetching unit inventories failed', error);
        unitInventoriesDispatch({ type: 'FETCH_FAILED_UNIT_INVENTORY' });
      }
    };
    if (unitInventoriesState.shouldFetch) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitInventoriesState.shouldFetch]);

  useSubscriptions<
    OnCreateUnitInventorySubscription,
    OnUpdateUnitInventorySubscription,
    OnDeleteUnitInventorySubscription
  >(
    onCreateUnitInventory,
    onUpdateUnitInventory,
    onDeleteUnitInventory,
    (data) => {
      unitInventoriesDispatch({
        type: 'ADD_UNIT_INVENTORY',
        payload: { unitInventory: data.onCreateUnitInventory as UnitInventory },
      });
    },
    (data) => {
      unitInventoriesDispatch({
        type: 'UPDATE_UNIT_INVENTORY',
        payload: { unitInventory: data.onUpdateUnitInventory as UnitInventory },
      });
    },
    (data) => {
      const { id } = data.onDeleteUnitInventory as UnitInventory;
      unitInventoriesDispatch({
        type: 'DELETE_UNIT_INVENTORY',
        payload: { id },
      });
    }
  );

  const setFetchUnitInventory = () => {
    unitInventoriesDispatch({ type: 'SHOULD_FETCH_UNIT_INVENTORY' });
  };

  const createUnitInventory = async (input: Omit<UnitInventory, 'id' | 'clientId' | 'readId'>) => {
    const unitInventory = await mutation<UnitInventory, CreateMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateInput),
        clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
        readId: getReadId(clientId!, ENTITY_MODEL_NAME),
      },
    });
    unitInventoriesDispatch({ type: 'ADD_UNIT_INVENTORY', payload: { unitInventory } });
    return unitInventory;
  };

  const updateUnitInventory = async (original: UnitInventory, updates: Partial<UnitInventory>) => {
    const result = await mutation<UnitInventory>(updateMutation, {
      input: { ...cleanInputUpdate({ id: original.id, _version: original._version, ...updates }, false) },
    });
    unitInventoriesDispatch({ type: 'UPDATE_UNIT_INVENTORY', payload: { unitInventory: result } });
    return result;
  };

  /**
   * Delete the unit inventory.
   * Perform a hard delete if the unit of the unitInventory is not linked to any Lease
   * Otherwise flag the unitInventory to the flaggedDate (= today if none is given ).
   */
  const deleteUnitInventory = async (id: string, flaggedDate?: DateTime) => {
    if (!buildingsUnitsDetailsDelete) {
      return;
    }
    const unitInventory = getUnitInventory(id);
    if (isNil(unitInventory)) {
      return;
    }
    const unitOfUnitInventory = getUnit(unitInventory.unitId);
    if (isNil(unitOfUnitInventory)) {
      return;
    }
    const shouldFlag = !isNil(unitOfUnitInventory.leases) && !isEmpty(unitOfUnitInventory.leases);
    if (shouldFlag) {
      await updateUnitInventory(unitInventory, {
        deletedAt: flaggedDate ? fromDateTime(flaggedDate) : fromDateTime(todayDateTime()),
      });
      return;
    }
    await deleteAndHideEntity<UnitInventory>(unitInventory, deleteUnitInventoryMutation);
  };

  const getUnitInventory = (id: string): UnitInventory | undefined => {
    if (unitInventoriesLoading) {
      return undefined;
    }
    if (isNil(unitInventories)) {
      return;
    }
    return unitInventories.find((u) => u.id === id);
  };

  /**
   * If a date is specified, returns the unit inventories that existed at date
   */
  const getUnitInventoriesFor = (unitId: string, date?: DateTime): UnitInventory[] => {
    if (unitInventoriesLoading) {
      return [];
    }
    if (isNil(unitInventories)) {
      return [];
    }
    if (isNil(date)) return unitInventories.filter((u) => u.unitId === unitId);

    return unitInventories.filter((u) => {
      if (u.unitId === unitId) {
        const notDeletedBeforeDate =
          isNil(u.deletedAt) ||
          (!isNil(u.deletedAt) && isInstantAfterOrEqual(calendarDateTimeFromTimestamp(u.deletedAt), date));
        return notDeletedBeforeDate;
      }
      return false;
    });
  };

  const values = useMemo(
    () => ({
      ...unitInventoriesState,
      unitInventoriesLoading: unitInventoriesState.loading,
      getUnitInventory,
      getUnitInventoriesFor,
      createUnitInventory,
      updateUnitInventory,
      deleteUnitInventory,
      unitInventoriesError: undefined,
      setFetchUnitInventory,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitInventoriesState, getUnit]
  );

  return <UnitInventoryContext.Provider value={values}>{children}</UnitInventoryContext.Provider>;
};

export const useUnitInventories = (shouldFetchOnMount: boolean = true): UnitInventoryContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<UnitInventoryContext | null>(UnitInventoryContext);

  if (isNil(context)) {
    throw new Error('`useUnitInventories` hook must be used within a `UnitInventoryContextProvider` component');
  }

  useEffect(() => {
    if (shouldFetchOnMount && !firstRender.current && isNil(context.unitInventories)) {
      context.setFetchUnitInventory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender, shouldFetchOnMount]);

  return context as UnitInventoryContext;
};
